import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useCreateDocExtractionEvalRunMutation,
  useEstimateDocExtractionEvalRunMutation,
  type EvalSetFieldsFragment,
} from "../../generated/graphql";
import {
  CANON_RETRIEVAL,
  DEFAULT_CONCURRENCY,
  DEFAULT_MAX_OUTPUT_TOKENS,
  EVAL_PROVIDERS,
  KNOWN_MODELS_BY_PROVIDER,
  REASONING_EFFORT_OPTIONS,
  type EvalPricingOverride,
  type EvalProvider,
  type EvalRunConfig,
  type ExecutionMode,
} from "./eval-config";

type Estimate = {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  usd: number | null;
  pricingSource: string;
  tokenProfileSource: string;
};

const numOrUndefined = (raw: string): number | undefined =>
  raw.trim() === "" || !Number.isFinite(Number(raw)) ? undefined : Number(raw);

/**
 * Configures and launches a single-arm eval run against the selected set:
 * provider/model, reasoning effort, retrieval knobs, image + token + concurrency
 * settings, a sync/batch toggle, and a pre-launch cost estimate. Launch stays
 * disabled until a fresh estimate resolves with a price — an unknown model must
 * supply a pricing override before it can run.
 */
const RunLauncher = ({
  set,
  onLaunched,
}: {
  readonly set: EvalSetFieldsFragment;
  readonly onLaunched: (runId: string) => void;
}) => {
  const [name, setName] = useState("");
  const [provider, setProvider] = useState<EvalProvider>("openai");
  const [modelId, setModelId] = useState<string>(
    KNOWN_MODELS_BY_PROVIDER.openai[0] ?? "",
  );
  const [reasoningEffort, setReasoningEffort] = useState("");
  const [k, setK] = useState(String(CANON_RETRIEVAL.k));
  const [mmrLambda, setMmrLambda] = useState(String(CANON_RETRIEVAL.mmrLambda));
  const [overfetch, setOverfetch] = useState(String(CANON_RETRIEVAL.overfetch));
  const [includePageImages, setIncludePageImages] = useState(true);
  const [maxOutputTokens, setMaxOutputTokens] = useState(
    String(DEFAULT_MAX_OUTPUT_TOKENS),
  );
  const [concurrency, setConcurrency] = useState(String(DEFAULT_CONCURRENCY));
  const [executionMode, setExecutionMode] = useState<ExecutionMode>("sync");

  // Pricing override inputs (only used when the model has no known price).
  const [overrideInput, setOverrideInput] = useState("");
  const [overrideOutput, setOverrideOutput] = useState("");
  const [overrideBatchInput, setOverrideBatchInput] = useState("");
  const [overrideBatchOutput, setOverrideBatchOutput] = useState("");

  const [estimate, setEstimate] = useState<Estimate | null>(null);
  // Identifies the estimate request the current estimate belongs to. Bumped on
  // every config/mode/set change so an in-flight estimate for a prior config is
  // discarded when it resolves rather than applied to the current one.
  const estimateRequestId = useRef(0);

  const [runEstimate, { loading: estimating, error: estimateError }] =
    useEstimateDocExtractionEvalRunMutation();
  const [runLaunch, { loading: launching, error: launchError }] =
    useCreateDocExtractionEvalRunMutation();

  const pricingOverride = useMemo((): EvalPricingOverride | undefined => {
    const input = numOrUndefined(overrideInput);
    const output = numOrUndefined(overrideOutput);
    if (input === undefined || output === undefined) return undefined;
    const batchInput = numOrUndefined(overrideBatchInput);
    const batchOutput = numOrUndefined(overrideBatchOutput);
    return {
      inputPerMTok: input,
      outputPerMTok: output,
      ...(batchInput === undefined ? {} : { batchInputPerMTok: batchInput }),
      ...(batchOutput === undefined ? {} : { batchOutputPerMTok: batchOutput }),
    };
  }, [overrideInput, overrideOutput, overrideBatchInput, overrideBatchOutput]);

  const config = useMemo((): EvalRunConfig => {
    return {
      provider,
      modelId: modelId.trim(),
      ...(reasoningEffort === "" ? {} : { reasoningEffort }),
      retrieval: {
        k: Number(k),
        mmrLambda: Number(mmrLambda),
        overfetch: Number(overfetch),
      },
      includePageImages,
      maxOutputTokens: Number(maxOutputTokens),
      concurrency: Number(concurrency),
      ...(pricingOverride === undefined ? {} : { pricingOverride }),
    };
  }, [
    provider,
    modelId,
    reasoningEffort,
    k,
    mmrLambda,
    overfetch,
    includePageImages,
    maxOutputTokens,
    concurrency,
    pricingOverride,
  ]);

  // Any config, mode, or set change invalidates a prior estimate — force a
  // re-estimate before launch so the displayed cost always matches what runs.
  const configKey = JSON.stringify(config);
  useEffect(() => {
    estimateRequestId.current += 1;
    setEstimate(null);
  }, [configKey, executionMode, set.id]);

  const handleProviderChange = (next: EvalProvider) => {
    setProvider(next);
    setModelId(KNOWN_MODELS_BY_PROVIDER[next][0] ?? "");
  };

  const handleEstimate = async () => {
    const requestId = estimateRequestId.current;
    const res = await runEstimate({
      variables: { input: { evalSetId: set.id, config, executionMode } },
    });
    // The config changed while this estimate was in flight — its price is for a
    // config that is no longer selected, so it must not enable launch.
    if (estimateRequestId.current !== requestId) return;
    const data = res.data?.estimateDocExtractionEvalRun;
    if (data !== undefined && data !== null) {
      setEstimate({
        inputTokens: data.inputTokens,
        outputTokens: data.outputTokens,
        totalTokens: data.totalTokens,
        usd: data.usd ?? null,
        pricingSource: data.pricingSource,
        tokenProfileSource: data.tokenProfileSource,
      });
    }
  };

  const handleLaunch = async () => {
    const res = await runLaunch({
      variables: {
        input: { evalSetId: set.id, name: name.trim(), config, executionMode },
      },
    });
    const created = res.data?.createDocExtractionEvalRun;
    if (created !== undefined && created !== null) {
      onLaunched(created.evalRunId);
    }
  };

  const needsOverride = estimate?.usd === null;
  const hasPricedEstimate = (estimate?.usd ?? null) !== null;
  const canLaunch =
    name.trim().length > 0 &&
    modelId.trim().length > 0 &&
    hasPricedEstimate &&
    !launching;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography gutterBottom variant="h6">
        Launch a run
      </Typography>
      <Typography gutterBottom variant="body2" color="text.secondary">
        Target set: <strong>{set.name}</strong> ({set.documentCount} docs)
      </Typography>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Run name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="provider-label">Provider</InputLabel>
            <Select
              labelId="provider-label"
              label="Provider"
              value={provider}
              onChange={(e) => {
                handleProviderChange(e.target.value as EvalProvider);
              }}
            >
              {EVAL_PROVIDERS.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Autocomplete
            freeSolo
            size="small"
            options={[...KNOWN_MODELS_BY_PROVIDER[provider]]}
            value={modelId}
            renderInput={(params) => <TextField {...params} label="Model id" />}
            onInputChange={(_e, value) => {
              setModelId(value);
            }}
          />
        </Grid>

        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel id="effort-label">Reasoning effort</InputLabel>
            <Select
              labelId="effort-label"
              label="Reasoning effort"
              value={reasoningEffort}
              onChange={(e) => {
                setReasoningEffort(e.target.value);
              }}
            >
              {REASONING_EFFORT_OPTIONS.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt === "" ? "(provider default)" : opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Retrieval k"
            value={k}
            onChange={(e) => {
              setK(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="MMR λ"
            inputProps={{ step: 0.1, min: 0, max: 1 }}
            value={mmrLambda}
            onChange={(e) => {
              setMmrLambda(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Overfetch"
            value={overfetch}
            onChange={(e) => {
              setOverfetch(e.target.value);
            }}
          />
        </Grid>

        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Max output tokens"
            value={maxOutputTokens}
            onChange={(e) => {
              setMaxOutputTokens(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Concurrency"
            value={concurrency}
            onChange={(e) => {
              setConcurrency(e.target.value);
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControlLabel
            control={
              <Switch
                checked={includePageImages}
                onChange={(e) => {
                  setIncludePageImages(e.target.checked);
                }}
              />
            }
            label="Include page images"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControlLabel
            control={
              <Switch
                checked={executionMode === "batch"}
                onChange={(e) => {
                  setExecutionMode(e.target.checked ? "batch" : "sync");
                }}
              />
            }
            label="Batch mode"
          />
        </Grid>
      </Grid>

      {executionMode === "batch" && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Batch results can take up to 24 hours.
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          disabled={estimating || modelId.trim().length === 0}
          onClick={handleEstimate}
        >
          {estimating ? "Estimating…" : "Estimate"}
        </Button>
        <Button
          variant="contained"
          disabled={!canLaunch}
          onClick={handleLaunch}
        >
          {launching ? "Launching…" : "Launch"}
        </Button>
      </Stack>

      {estimateError !== undefined && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Estimate failed: {estimateError.message}
        </Alert>
      )}
      {launchError !== undefined && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Launch failed: {launchError.message}
        </Alert>
      )}

      {estimate !== null && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Cost estimate</Typography>
          <Typography variant="body2">
            {estimate.totalTokens.toLocaleString()} tokens (
            {estimate.inputTokens.toLocaleString()} in /{" "}
            {estimate.outputTokens.toLocaleString()} out) ·{" "}
            {estimate.usd === null
              ? "price unknown"
              : `$${estimate.usd.toFixed(2)}`}{" "}
            · pricing: {estimate.pricingSource} · tokens:{" "}
            {estimate.tokenProfileSource}
          </Typography>
        </Box>
      )}

      {needsOverride && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1 }} />
          <Alert severity="info" sx={{ mb: 1 }}>
            This model has no known price. Enter a pricing override (USD per 1M
            tokens), then re-estimate to enable launch.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Input / 1M"
                value={overrideInput}
                onChange={(e) => {
                  setOverrideInput(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Output / 1M"
                value={overrideOutput}
                onChange={(e) => {
                  setOverrideOutput(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Batch input / 1M (opt)"
                value={overrideBatchInput}
                onChange={(e) => {
                  setOverrideBatchInput(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Batch output / 1M (opt)"
                value={overrideBatchOutput}
                onChange={(e) => {
                  setOverrideBatchOutput(e.target.value);
                }}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

export default RunLauncher;
